<template>
  <v-text-field
    v-model="formattedText"
    :rules="rules"
    :label="label ? label + (required ? '' : ' (optional)') : ''"
    :validate-on-blur="true"
    :suffix="suffix"
    class="percentage-text-field"
    :disabled="disabled"
    :color="$colors.penChecksTeal"
    @focus="isFocused = true"
    @blur="inputBlurred"
    @change="$emit('change', floatValue)"
    @keyup="keyUp"
    :data-id="dataId"
  ></v-text-field>
</template>
<script>
export default {
  props: {
    value: {},
    label: {
      type: String,
    },
    required: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    minValue: {
      type: Number,
    },
    maxValue: {
      type: Number,
    },
    allowZero: {
      type: Boolean,
    },
    wholeNumbers: {
      type: Boolean,
    },
    // Percent decimal places to preserve. When set, the display shows this many decimals
    // and floatValue rounds the fraction to decimals+2 (so it never rounds coarser than the
    // display). Omitted = legacy behavior (2 display / 3 fraction) so existing consumers are
    // unchanged. Needed for DECIMAL(7,5) rates like 6.99% / 4.466% that the legacy 0.1%
    // granularity silently truncated.
    decimals: {
      type: Number,
      default: null,
    },
    dataId: {
      type: String,
      required: true,
      validator(value) {
        return value.length == 32;
      },
    },
  },

  data: function () {
    return {
      formattedText: null,
      isFocused: false,

      rules: [
        () => {
          if (this.required && this.floatValue == null)
            return "Please enter an value.";
          return true;
        },
        () => {
          if (!this.allowZero && this.floatValue == 0)
            return "Please enter a non-zero value.";
          return true;
        },
        () => {
          if (!this.allowNegative && this.floatValue < 0)
            return "Please enter a positive value.";
          return true;
        },
        () => {
          if (
            this.minValue != null &&
            this.floatValue != null &&
            this.floatValue < this.minValue
          )
            return (
              "Must be at least " +
              Math.roundProperly(this.minValue * 100, 2) +
              "%"
            );
          return true;
        },
        () => {
          if (
            this.maxValue != null &&
            this.floatValue != null &&
            this.floatValue > this.maxValue
          )
            return (
              "Must not exceed " +
              Math.roundProperly(this.maxValue * 100, 2) +
              "%"
            );
          return true;
        },
      ],
    };
  },

  computed: {
    floatValue: function () {
      if (this.formattedText == null) return null;
      var percentageFloat =
        parseFloat(Number.numberFormatHelper(this.formattedText)) * 0.01;
      return Math.roundProperly(
        percentageFloat,
        this.decimals == null ? 3 : this.decimals + 2
      );
    },
    suffix: function () {
      return this.isFocused || this.formattedText ? "%" : null;
    },
  },

  watch: {
    value: function () {
      if (this.value != this.floatValue) {
        this.syncFormattedFromValue();
      }
    },
    floatValue: function () {
      if (this.value !== this.floatValue) {
        this.$emit("input", this.floatValue);
      }
    },
  },

  methods: {
    keyUp: function (e) {
      this.$emit("keyup", e);

      this.textChanged(e);
    },
    textChanged: function (e) {
      var selectionStart = e.target.selectionStart;
      var selectionEnd = e.target.selectionEnd;
      var value = e.target.value;

      if (
        this.formattedText == null ||
        (this.formattedText != null && this.formattedText.trim() == "")
      ) {
        this.formattedText = null;
        return;
      }

      var isPasting = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() == "v";

      // Dont format if not at end of string (or pasting)
      if (
        !isPasting &&
        (selectionStart != selectionEnd || selectionStart < value.length)
      ) {
        return;
      }

      if (e.key != "." && e.keyCode != 8) {
        this.formatText();
      }

      this.setCursorPosition(selectionEnd);
    },
    inputBlurred: function () {
      this.isFocused = false;
      this.formatText();
    },
    setCursorPosition(selectionEnd) {
      if (this.$children[0].$refs.input && !Number.isNaN(selectionEnd)) {
        this.$nextTick(() => {
          this.$children[0].$refs.input.selectionEnd = selectionEnd;
        });
      }
    },
    // Sync display text from the bound value; a null/NaN value stays blank rather than being
    // coerced to "0" (numberFormatHelper turns "NaN" into "0"), which would mask an unset rate.
    syncFormattedFromValue: function () {
      var parsed = parseFloat(this.value);
      if (this.value == null || Number.isNaN(parsed)) {
        this.formattedText = null;
        return;
      }
      this.formattedText = parsed * 100 + "";
      this.formatText();
    },
    formatText: function () {
      if (this.formattedText)
        this.formattedText = Number.numberFormatHelper(this.formattedText, {
          commas: true,
        });

      if (this.formattedText != null && this.formattedText.toString() == "NaN")
        this.formattedText = null;

      if (this.formattedText != null && this.formattedText != "") {
        this.formattedText = String(
          Math.roundProperly(
            this.formattedText,
            this.decimals == null ? 2 : this.decimals
          )
        );
      }
    },
  },

  mounted: function () {
    this.syncFormattedFromValue();
  },
};
</script>

<style lang="scss"></style>
